// Port tab (04 §5.12): trade resources for Credits, one trade at a time.
import type { FullSystem, Resources } from '@stars-and-ships/api-types';
import { useState } from 'react';
import { formatDuration, formatNumber, RESOURCE_KEYS, RESOURCE_LABELS } from '../../labels';
import { affordable } from '../../state/live';
import { tradeCredits } from '../../state/systemCache';
import { Button, NumberField, Panel } from '../ui';
import { parseAmount } from './shared';
import type { SystemActions } from './types';

export function PortTab({
  system,
  have,
  now,
  actions,
}: {
  system: FullSystem;
  have: Resources;
  now: number;
  actions: SystemActions | undefined;
}) {
  const terms = system.tradingPort;
  const [input, setInput] = useState({ metal: '', gas: '', supplies: '' });
  const offer: Resources = {
    metal: parseAmount(input.metal),
    gas: parseAmount(input.gas),
    supplies: parseAmount(input.supplies),
  };
  const total = offer.metal + offer.gas + offer.supplies;
  const credits = tradeCredits(terms, total);
  const problem =
    total < terms.minResources
      ? `Trade at least ${formatNumber(terms.minResources)} resources.`
      : total > terms.maxResources
        ? `This port takes at most ${formatNumber(terms.maxResources)} per trade.`
        : !affordable(have, offer)
          ? 'Not enough resources in storage.'
          : null;
  const running = system.portTrade;

  return (
    <Panel title="Trading Port">
      {terms.level === 0 ? (
        <p className="text-sm text-muted">Build a Trading Port to trade resources for Credits.</p>
      ) : running ? (
        <div data-port-trade={running.id} className="space-y-1 text-sm">
          <p className="text-text">
            Trading{' '}
            {formatNumber(
              running.resourcesIn.metal + running.resourcesIn.gas + running.resourcesIn.supplies
            )}{' '}
            resources for {formatNumber(running.creditsOut)} Credits
          </p>
          <p className="font-mono text-xs tabular-nums text-info">
            {Date.parse(running.completesAt) > now
              ? `ready in ${formatDuration(Date.parse(running.completesAt) - now)}`
              : 'arriving…'}
          </p>
        </div>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!problem) actions?.trade(offer);
          }}
        >
          <div className="grid grid-cols-3 gap-2">
            {RESOURCE_KEYS.map((key) => (
              <NumberField
                key={key}
                label={RESOURCE_LABELS[key]}
                aria-label={`Trade ${RESOURCE_LABELS[key]}`}
                min={0}
                name={`trade-${key}`}
                value={input[key]}
                onChange={(e) => setInput((prev) => ({ ...prev, [key]: e.target.value }))}
              />
            ))}
          </div>
          <p className="text-xs text-muted">
            {formatNumber(total)} resources →{' '}
            <span className="text-accent">{formatNumber(credits)} Credits</span> in{' '}
            {formatDuration(terms.durationMs)} · {formatNumber(terms.creditsPer1000)} per 1,000 · up
            to {formatNumber(terms.maxResources)}
          </p>
          {total > 0 && problem ? <p className="text-xs text-danger">{problem}</p> : null}
          <Button
            type="submit"
            className="w-full"
            disabled={!actions || problem !== null}
            pending={actions?.pending.trade ?? false}
          >
            Trade for Credits
          </Button>
        </form>
      )}
    </Panel>
  );
}
